import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AlfatekLogo from "@/components/AlfatekLogo";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { ClipboardList } from "lucide-react";

const WhatsAppIcon = ({ size = 14, className = "" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className} aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

export const PublicHeader = () => {
    const { count } = useCart();
    return (
        <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-neutral-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <Link to="/" data-testid="public-header-logo" className="flex items-center gap-3">
                    <AlfatekLogo className="h-8" />
                    <span className="hidden sm:inline text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">Multi-marca industrial</span>
                </Link>
                <nav className="flex items-center gap-6 text-sm">
                    <Link data-testid="nav-produtos" to="/produtos" className="text-neutral-700 hover:text-brand-blue font-medium">Produtos</Link>
                    <Link data-testid="nav-lista" to="/lista" className="relative inline-flex items-center gap-2 text-neutral-700 hover:text-brand-blue font-medium">
                        <ClipboardList size={16} /> <span className="hidden sm:inline">Minha lista</span>
                        {count > 0 && <span className="ml-1 min-w-[20px] h-5 px-1 bg-brand-yellow text-ink font-mono text-[10px] font-bold flex items-center justify-center">{count}</span>}
                    </Link>
                    <Link data-testid="nav-admin" to="/admin/login" className="hidden sm:inline text-neutral-500 hover:text-brand-blue text-xs font-mono uppercase tracking-widest">Admin</Link>
                </nav>
            </div>
        </header>
    );
};

const BrandsFooterList = () => {
    const [brands, setBrands] = useState([]);
    useEffect(() => {
        let mounted = true;
        api.get("/public/brands").then((r) => {
            if (!mounted) return;
            setBrands((r.data || []).map((b) => b.name).filter(Boolean));
        }).catch(() => setBrands([]));
        return () => { mounted = false; };
    }, []);
    if (brands.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-neutral-700 font-heading font-bold text-sm">
            {brands.map((n, i) => <span key={i} data-testid={`footer-brand-${i}`} className="uppercase tracking-wide">{n}</span>)}
        </div>
    );
};

export const PublicFooter = () => (
    <footer className="mt-24 border-t border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-4">Marcas parceiras</div>
            <BrandsFooterList />
        </div>
        <div className="border-t border-neutral-200 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid gap-6 md:grid-cols-3 items-start">
            <div className="flex flex-col gap-3">
                <AlfatekLogo className="h-8" />
                <p className="text-sm text-neutral-500 max-w-sm">Catálogo multi-marca de equipamentos industriais. Um só lugar para especificar, comparar e cotar.</p>
            </div>
            <div className="text-sm text-neutral-500 font-mono">
                <div className="uppercase tracking-widest text-xs text-neutral-400 mb-2">Cobertura</div>
                <div>41 marcas · 24 categorias · 245+ produtos</div>
            </div>
            <div className="text-sm text-neutral-500 font-mono md:text-right">
                <div className="uppercase tracking-widest text-xs text-neutral-400 mb-2">contato</div>
                <div className="mb-2">contato@alfatek.com.br</div>
                <div className="flex flex-col gap-1.5 md:items-end">
                    <a
                        href="https://wa.me/5517997712161"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-whatsapp-alfatek"
                        className="inline-flex items-center gap-2 text-neutral-700 hover:text-[#25D366] transition-colors"
                    >
                        <WhatsAppIcon size={14} className="text-[#25D366]" />
                        <span>Alfatek: +55 17 99771-2161</span>
                    </a>
                    <a
                        href="https://wa.me/5517996382369"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-whatsapp-redparts"
                        className="inline-flex items-center gap-2 text-neutral-700 hover:text-[#25D366] transition-colors"
                    >
                        <WhatsAppIcon size={14} className="text-[#25D366]" />
                        <span>Red Parts: +55 17 99638-2369</span>
                    </a>
                </div>
            </div>
        </div>
        <div className="text-center text-xs text-neutral-400 font-mono py-4 border-t border-neutral-200">© {new Date().getFullYear()} ALFATEK · Todos os direitos reservados</div>
    </footer>
);
